/**
 * Reset Password Page Component
 * Hope SACCO Frontend
 * Handles Firebase password reset action codes from email links
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../config/firebase';
import './ForgotPasswordPage.css'; // Reuse same styles

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [invalidLink, setInvalidLink] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  // Extract oobCode from URL — Firebase sends ?mode=resetPassword&oobCode=XXXX
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    const verifyCode = async () => {
      if (!oobCode || mode !== 'resetPassword') {
        setInvalidLink(true);
        setIsVerifying(false);
        return;
      }

      try {
        const email = await verifyPasswordResetCode(auth, oobCode);
        setUserEmail(email);
        setIsVerifying(false);
      } catch (err) {
        console.error('Code verification failed:', err);
        setInvalidLink(true);
        setIsVerifying(false);
      }
    };

    verifyCode();
  }, [oobCode, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await confirmPasswordReset(auth, oobCode, password);
      setIsSuccess(true);
    } catch (err) {
      console.error('Password reset failed:', err);
      if (err.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (err.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid or has already been used.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 8 characters.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Loading while verifying the code
  if (isVerifying) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-box">
            <div className="success-icon">⏳</div>
            <h1>Verifying Link...</h1>
            <p>Please wait while we verify your reset link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid or expired link
  if (invalidLink) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-box">
            <div className="success-icon">❌</div>
            <h1>Invalid Reset Link</h1>
            <p className="success-message">
              This password reset link is invalid or has expired.
            </p>
            <p className="help-text">
              Reset links expire after 1 hour. Please request a new one.
            </p>
            <button
              onClick={() => navigate('/forgot-password')}
              className="btn btn-primary btn-full"
            >
              Request New Reset Link
            </button>
            <div className="back-to-login" style={{ marginTop: '12px' }}>
              <button
                onClick={() => navigate('/login')}
                className="link-button"
              >
                ← Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="forgot-password-page">
        <div className="forgot-password-container">
          <div className="forgot-password-box">
            <div className="success-icon">✅</div>
            <h1>Password Reset!</h1>
            <p className="success-message">
              Your password has been successfully reset for <strong>{userEmail}</strong>.
            </p>
            <p className="help-text">
              You can now log in with your new password.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="btn btn-primary btn-full"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main reset form
  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-box">
          <div className="forgot-password-header">
            <div className="icon">🔐</div>
            <h1>Set New Password</h1>
            {userEmail && (
              <p>Resetting password for <strong>{userEmail}</strong></p>
            )}
          </div>

          {error && (
            <div className="alert alert-error">
              <span className="alert-icon">⚠️</span>
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="forgot-password-form" noValidate>
            <div className="form-group">
              <label htmlFor="password">
                New Password <span className="required">*</span>
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(''); }}
                  className={error ? 'input-error' : ''}
                  placeholder="Enter new password (min. 8 characters)"
                  disabled={isLoading}
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute', right: '10px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0'
                  }}
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">
                Confirm New Password <span className="required">*</span>
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                className={error ? 'input-error' : ''}
                placeholder="Re-enter new password"
                disabled={isLoading}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <><span className="spinner"></span>Resetting...</>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>

          <div className="back-to-login">
            <button
              onClick={() => navigate('/login')}
              className="link-button"
              disabled={isLoading}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;